import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import '../styles/klinks-list.css';

interface Klink {
  klink: string;
  url: string;
  uses: number;
  owner: string;
}

const KlinksListPage: React.FC = () => {
  const [klinks, setKlinks] = useState<Klink[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const { apiFetch, user } = useAuth();
  const navigate = useNavigate();

  const fetchKlinks = async (search: string = '', currentOffset: number = 0) => {
    try {
      setIsLoading(true);
      setError(null);
      const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';
      const offsetParam = currentOffset > 0 ? `&offset=${currentOffset}` : '';
      const response = await apiFetch(`${process.env.REACT_APP_API_BASE_URL}/api/klinks?${searchParam}${offsetParam}`);
      const data = await response.json();
      const klinksData = data || [];
      setKlinks(klinksData);
      
      // If we get less than 20 results, we've reached the end
      setHasMore(klinksData.length === 20);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch klinks');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setOffset(0);
    fetchKlinks();
  }, [apiFetch]);

  const handleSearch = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      setOffset(0);
      fetchKlinks(searchTerm);
    }
  };

  const handlePrevious = () => {
    const newOffset = Math.max(0, offset - 20);
    setOffset(newOffset);
    fetchKlinks(searchTerm, newOffset);
  };

  const handleNext = () => {
    const newOffset = offset + 20;
    setOffset(newOffset);
    fetchKlinks(searchTerm, newOffset);
  };

  if (isLoading && klinks.length === 0) {
    return (
      <div className="loading-container">
        Loading your klinks...
      </div>
    );
  }

  if (error) {
    return (
      <div className="card error-card">
        <h2>Error</h2>
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div>
      <input
        autoFocus
        type="text"
        placeholder="Search k-links or URLs... (Press Enter to search)"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        onKeyDown={handleSearch}
        className="search-input"
      />

      {klinks.length === 0 ? (
        <div className="card empty-state">
          {searchTerm ? 'No k-links found matching your search.' : 'No k-links created yet.'}
        </div>
      ) : (
        <>
          <div className="card table-container">
            <table>
              <thead>
                <tr>
                  <th>k-link</th>
                  <th>URL</th>
                  <th className="uses-cell">Uses</th>
                  <th className="owner-cell">Owner</th>
                </tr>
              </thead>
              <tbody>
                {klinks.map((k) => (
                  <tr key={k.klink}>
                    <td>
                      <a 
                        href={`/klinks/${k.klink}`}
                        className="klink-link"
                      >
                        {k.klink}
                      </a>
                    </td>
                    <td className="url-cell">
                      <a 
                        href={k.url.startsWith('http') ? k.url : `https://${k.url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="url-link"
                      >
                        {k.url}
                      </a>
                    </td>
                    <td className="uses-cell">{k.uses}</td>
                    <td className="owner-cell">{k.owner}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="pagination-container" style={{ display: 'flex', justifyContent: 'space-between' }}>
            <button 
              onClick={handlePrevious} 
              disabled={isLoading || offset === 0}
              className="pagination-button"
            >
              Previous
            </button>
            <button 
              onClick={handleNext} 
              disabled={isLoading || !hasMore}
              className="pagination-button"
            >
              Next
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default KlinksListPage;
